import React, { useEffect, useState, useRef, useMemo } from 'react';
import axios from 'axios';
import SignatureCanvas from 'react-signature-canvas';
import LoadingSpinner from '../components/LoadingSpinner';

const ROLES = ['Student', 'Instructor', 'Admin', 'Coordinator', 'Registrar', 'Super - Admin', 'System Admin', 'Owner', 'Super - System Admin'];
// Base role list for User Management role-selection UI
const USER_MANAGEMENT_VISIBLE_ROLES = ROLES.filter(
  (role) => role !== 'Super - System Admin' && role !== 'Owner'
);
const getAssignableRoles = (currentUserRoles = []) =>
  currentUserRoles.includes('Owner')
    ? [...USER_MANAGEMENT_VISIBLE_ROLES, 'Owner']
    : USER_MANAGEMENT_VISIBLE_ROLES;

const escapeCsvField = (value) => {
  const str = value == null ? '' : String(value);
  if (/[",\n\r]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const UserManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [allAccounts, setAllAccounts] = useState([]); // Store original data for filtering
  const [accessCards, setAccessCards] = useState([]);
  const [allAccessCards, setAllAccessCards] = useState([]); // Store original data for filtering (needed for filtering accounts)
  const [enrollments, setEnrollments] = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]); // Store original data for filtering (needed for filtering accounts)
  const [schools, setSchools] = useState([]);
  const [concentrations, setConcentrations] = useState([]);
  const [associations, setAssociations] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  const [currentUserRoles, setCurrentUserRoles] = useState([]); // Store current user's roles from all access cards
  
  // Filter states
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedRole, setSelectedRole] = useState('All Roles');
  const [selectedAssociation, setSelectedAssociation] = useState('All Associations');
  const [selectedPeriod, setSelectedPeriod] = useState('All Terms');
  const assignableRoles = useMemo(() => getAssignableRoles(currentUserRoles), [currentUserRoles]);
  
  // Modal states
  const [showAccountModal, setShowAccountModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [includeGradeLevel, setIncludeGradeLevel] = useState(true);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      
      // First, fetch critical data (Accounts, Access Cards, Schools) - these determine when loading stops
      // Handle each request separately to ensure loading stops even if one fails
      let accountsRes, accessCardsRes, schoolsRes, communityRes;
      
      try {
        accountsRes = await axios.get('/api/user-management/accounts', { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('Failed to fetch accounts:', err);
        accountsRes = { data: { accounts: [] } };
      }
      
      try {
        accessCardsRes = await axios.get('/api/user-management/accesscards', { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('Failed to fetch access cards:', err);
        accessCardsRes = { data: { accesscards: [] } };
      }
      
      try {
        schoolsRes = await axios.get('/api/user-management/schools', { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('Failed to fetch schools:', err);
        schoolsRes = { data: { schools: [] } };
      }
      
      try {
        communityRes = await axios.get('/api/user-management/community', { headers: { Authorization: `Bearer ${token}` } });
      } catch (err) {
        console.error('Failed to fetch community/permissions:', err);
        communityRes = { data: { userPermissions: null } };
      }
      
      // Fetch current user data to get all of their roles
      let currentUserRes;
      try {
        currentUserRes = await axios.get('/api/user', { headers: { Authorization: `Bearer ${token}` } });
        const roles = (currentUserRes.data?.accesscards || [])
          .map((ac) => ac?.role)
          .filter(Boolean);
        setCurrentUserRoles([...new Set(roles)]);
      } catch (err) {
        console.error('Failed to fetch current user:', err);
      }
      
      const accountsData = accountsRes.data.accounts || [];
      const accessCardsData = accessCardsRes.data.accesscards || [];
      
      setAllAccounts(accountsData);
      setAllAccessCards(accessCardsData);
      setSchools(schoolsRes.data.schools || []);
      setUserPermissions(communityRes.data?.userPermissions || null);
      
      // Initialize enrollments as empty array for now (will be loaded in background)
      setAllEnrollments([]);
      setEnrollments([]);
      
      // Determine initial school selection for non-global users
      let initialSchool = selectedSchool;
      if (!initialSchool && communityRes.data?.userPermissions && !communityRes.data.userPermissions.isGlobal) {
        const userSchool = schoolsRes.data.schools?.find(school => 
          school.school_id === communityRes.data.userPermissions.school_id
        );
        if (userSchool) {
          initialSchool = userSchool;
        }
      }
      
      // Apply initial filters synchronously before stopping loading
      let filteredAccounts = [...accountsData];
      let filteredAccessCards = [...accessCardsData];
      let filteredEnrollments = [];
      
      // Apply school filter if needed
      if (initialSchool) {
        filteredAccessCards = filteredAccessCards.filter(card =>
          card.school_id === initialSchool.school_id
        );
        filteredAccounts = filteredAccounts.filter(account =>
          account.accesscards?.some(card => 
            filteredAccessCards.some(fc => fc.accesscard_id === card.accesscard_id)
          )
        );
      }
      
      // Set filtered data and school selection
      setAccounts(filteredAccounts);
      setAccessCards(filteredAccessCards);
      setEnrollments(filteredEnrollments);
      if (initialSchool && !selectedSchool) {
        setSelectedSchool(initialSchool);
      }
      
      // Stop loading spinner now that critical data is loaded and displayed
      setLoading(false);
      
      // Fetch remaining data in the background (these don't block the UI)
      try {
        const [
          enrollmentsRes,
          concentrationsRes,
          associationsRes,
          periodsRes
        ] = await Promise.all([
          axios.get('/api/user-management/enrollments', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/user-management/concentrations', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/user-management/associations', { headers: { Authorization: `Bearer ${token}` } }),
          axios.get('/api/user-management/periods', { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        
        const enrollmentsData = enrollmentsRes.data.enrollments || [];
        
        setAllEnrollments(enrollmentsData);
        setEnrollments(enrollmentsData);
        setConcentrations(concentrationsRes.data.concentrations || []);
        setAssociations(associationsRes.data.associations || []);
        setPeriods(periodsRes.data.periods || []);
      } catch (err) {
        console.error('Failed to fetch secondary data:', err);
        // Don't set error or loading state - these are non-critical
      }
    } catch (err) {
      setError('Failed to load user management data.');
      console.error(err);
      setLoading(false);
    }
  };

  const handleCreateAccount = () => {
    setEditingItem(null);
    setShowAccountModal(true);
  };

  const handleEditAccount = (account) => {
    // Check if account has Super - System Admin access cards
    const hasSuperSystemAdmin = account.accesscards?.some(
      card => card.role === 'Super - System Admin'
    );
    const hasOwnerRole = account.accesscards?.some(
      card => card.role === 'Owner'
    );
    
    // Prevent non-Super - System Admin users from opening Super - System Admin accounts
    if (hasSuperSystemAdmin && !currentUserRoles.includes('Super - System Admin')) {
      alert('You do not have permission to edit Super - System Admin accounts.');
      return;
    }
    if (hasOwnerRole && !currentUserRoles.includes('Owner')) {
      alert('You do not have permission to edit Owner accounts.');
      return;
    }
    
    setEditingItem(account);
    setShowAccountModal(true);
  };


  const handleDelete = async (type, id) => {
    if (!window.confirm(`Are you sure you want to delete this ${type.toLowerCase()}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/user-management/${type.toLowerCase()}/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh data
      await fetchAllData();
    } catch (err) {
      alert(`Failed to delete ${type.toLowerCase()}: ${err.response?.data?.error || err.message}`);
    }
  };

  /** Match Community.js: nested enrollments use association_id; flat /enrollments list uses association. */
  const enrollmentAssociationId = (en) =>
    en == null ? null : en.association_id != null ? en.association_id : en.association;

  // Same pattern as Community: school → role on account.accesscards → student association/period via nested enrollments
  const applyFilters = () => {
    let baseAccounts = [...allAccounts];

    if (selectedSchool) {
      baseAccounts = baseAccounts.filter((account) =>
        account.accesscards?.some((card) => card.school_id === selectedSchool.school_id)
      );
    }

    let filtered = baseAccounts;

    if (selectedRole !== 'All Roles') {
      filtered = filtered.filter((account) =>
        account.accesscards?.some((card) => card.role === selectedRole)
      );
    }

    if (selectedRole === 'Student' && selectedAssociation !== 'All Associations') {
      filtered = filtered.filter((account) =>
        account.accesscards?.some(
          (card) =>
            card.role === 'Student' &&
            card.enrollments?.some(
              (en) => String(enrollmentAssociationId(en)) === String(selectedAssociation)
            )
        )
      );
    }

    if (selectedRole === 'Student' && selectedPeriod !== 'All Terms') {
      filtered = filtered.filter((account) =>
        account.accesscards?.some(
          (card) =>
            card.role === 'Student' &&
            card.enrollments?.some((en) => String(en.period_id) === String(selectedPeriod))
        )
      );
    }

    filtered = filtered.sort((a, b) => {
      const fa = a.first_name ?? '';
      const fb = b.first_name ?? '';
      if (fa !== fb) return fa.localeCompare(fb);
      return (a.last_name ?? '').localeCompare(b.last_name ?? '');
    });

    setAccounts(filtered);

    // Keep access card / enrollment slices aligned with school (for any code that reads this state)
    let nextAccessCards = [...allAccessCards];
    let nextEnrollments = [...allEnrollments];
    if (selectedSchool) {
      nextAccessCards = nextAccessCards.filter((c) => c.school_id === selectedSchool.school_id);
    }
    nextEnrollments = nextEnrollments.filter((e) =>
      nextAccessCards.some((c) => c.accesscard_id === e.accesscard_id)
    );
    setAccessCards(nextAccessCards);
    setEnrollments(nextEnrollments);
  };

  // Filter handlers
  const handleSchoolSelect = (school) => {
    setSelectedSchool(school);
  };

  const handleShowAllSchools = () => {
    setSelectedSchool(null);
  };

  const handleRoleFilter = (role) => {
    setSelectedRole(role);
    // Reset association and period filters when changing roles
    if (role !== 'Student') {
      setSelectedAssociation('All Associations');
      setSelectedPeriod('All Terms');
    }
  };

  const handleAssociationFilter = (associationId) => {
    setSelectedAssociation(associationId);
  };

  const handlePeriodFilter = (periodId) => {
    setSelectedPeriod(periodId);
  };

  const getGradeLevelForAccount = (account) => {
    const gradeLevels = new Set();
    const cards = account.accesscards || [];
    for (const card of cards) {
      if (card.role !== 'Student') continue;
      if (selectedSchool && card.school_id !== selectedSchool.school_id) continue;
      for (const en of card.enrollments || []) {
        if (selectedRole === 'Student' && selectedAssociation !== 'All Associations') {
          if (String(enrollmentAssociationId(en)) !== String(selectedAssociation)) continue;
        }
        if (selectedRole === 'Student' && selectedPeriod !== 'All Terms') {
          if (String(en.period_id) !== String(selectedPeriod)) continue;
        }
        if (en.gradelevel != null && String(en.gradelevel).trim() !== '') {
          gradeLevels.add(String(en.gradelevel));
        }
      }
    }
    return gradeLevels.size > 0 ? Array.from(gradeLevels).join('; ') : '';
  };

  const handleDownloadCsv = () => {
    if (accounts.length === 0) {
      alert('No users to export.');
      return;
    }

    const headers = ['First Name', 'Last Name', 'Email'];
    if (includeGradeLevel) headers.push('Grade Level');

    const rows = accounts.map((acc) => {
      const cells = [
        escapeCsvField(acc.first_name || ''),
        escapeCsvField(acc.last_name || ''),
        escapeCsvField(acc.primary_email || '')
      ];
      if (includeGradeLevel) {
        cells.push(escapeCsvField(getGradeLevelForAccount(acc)));
      }
      return cells.join(',');
    });

    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `user-management-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    setShowCsvModal(false);
  };

  // Re-apply when filters or source data change (mirrors Community.js client-side filtering)
  useEffect(() => {
    if (!loading) {
      applyFilters();
    }
  }, [
    selectedSchool,
    selectedRole,
    selectedAssociation,
    selectedPeriod,
    loading,
    allAccounts,
    allAccessCards,
    allEnrollments
  ]);

  const filterChip = (active) => ({
    padding: '0.15rem 0.45rem',
    borderRadius: '3px',
    fontSize: '0.72rem',
    border: `1px solid ${active ? '#94a3b8' : '#e2e8f0'}`,
    backgroundColor: active ? '#e8ecf1' : '#fff',
    color: '#334155',
    cursor: 'pointer',
    fontWeight: active ? 600 : 400,
    lineHeight: 1.35
  });

  const filterRow = {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '0.3rem',
    marginBottom: '0.35rem'
  };

  const filterLabel = {
    fontSize: '0.65rem',
    color: '#64748b',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    width: '4.75rem',
    flexShrink: 0
  };

  return (
    <div style={{ padding: '2rem', maxWidth: 1400, margin: '0 auto' }}>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', gap: '1rem' }}>
            <h1 style={{ fontSize: '1.35rem', fontWeight: 600, margin: 0, color: '#1e293b' }}>User Management</h1>
            <button
              type="button"
              onClick={() => setShowCsvModal(true)}
              style={{
                padding: '0.45rem 0.85rem',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: '#fff',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '0.8125rem',
                fontWeight: 500,
                whiteSpace: 'nowrap'
              }}
            >
              Download CSV
            </button>
          </div>

          <div
            style={{
              marginBottom: '1rem',
              padding: '0.45rem 0.6rem',
              border: '1px solid #e5e7eb',
              borderRadius: '6px',
              backgroundColor: '#fafafa'
            }}
          >
            {userPermissions?.isGlobal && schools.length > 0 && (
              <div style={filterRow}>
                <span style={filterLabel}>School</span>
                <button type="button" onClick={handleShowAllSchools} style={filterChip(!selectedSchool)}>
                  All
                </button>
                {schools.map((school) => (
                  <button
                    type="button"
                    key={school.school_id}
                    onClick={() => handleSchoolSelect(school)}
                    style={filterChip(selectedSchool?.school_id === school.school_id)}
                  >
                    {school.school_name}
                  </button>
                ))}
              </div>
            )}

            <div style={{ ...filterRow, marginBottom: selectedRole === 'Student' ? '0.35rem' : 0 }}>
              <span style={filterLabel}>Role</span>
              <button type="button" onClick={() => handleRoleFilter('All Roles')} style={filterChip(selectedRole === 'All Roles')}>
                All
              </button>
              {assignableRoles.map((role) => (
                <button type="button" key={role} onClick={() => handleRoleFilter(role)} style={filterChip(selectedRole === role)}>
                  {role}
                </button>
              ))}
            </div>

            {selectedRole === 'Student' && periods.length > 0 && (
              <div style={filterRow}>
                <span style={filterLabel}>Term</span>
                <button type="button" onClick={() => handlePeriodFilter('All Terms')} style={filterChip(selectedPeriod === 'All Terms')}>
                  All
                </button>
                {periods.map((period) => (
                  <button
                    type="button"
                    key={period.period_id}
                    onClick={() => handlePeriodFilter(period.period_id)}
                    style={filterChip(selectedPeriod === period.period_id)}
                  >
                    {period.name}
                  </button>
                ))}
              </div>
            )}

            {selectedRole === 'Student' && associations.length > 0 && (
              <div style={{ ...filterRow, marginBottom: 0 }}>
                <span style={filterLabel}>Assoc.</span>
                <button
                  type="button"
                  onClick={() => handleAssociationFilter('All Associations')}
                  style={filterChip(selectedAssociation === 'All Associations')}
                >
                  All
                </button>
                {associations.map((association) => (
                  <button
                    type="button"
                    key={association.id}
                    onClick={() => handleAssociationFilter(association.id)}
                    style={filterChip(selectedAssociation === association.id)}
                  >
                    {association.title}
                  </button>
                ))}
              </div>
            )}
          </div>

          {error ? (
            <div style={{ color: 'red' }}>{error}</div>
          ) : (
              <AccountsTable 
                accounts={accounts} 
                onEdit={handleEditAccount}
                onDelete={handleDelete}
                onCreate={handleCreateAccount}
                currentUserRoles={currentUserRoles}
              />
          )}
        </>
      )}

      {/* Modals */}
      {showCsvModal && (
        <CsvDownloadModal
          accountCount={accounts.length}
          includeGradeLevel={includeGradeLevel}
          onIncludeGradeLevelChange={setIncludeGradeLevel}
          onDownload={handleDownloadCsv}
          onClose={() => setShowCsvModal(false)}
        />
      )}

      {showAccountModal && (
        <AccountModal
          account={editingItem}
          currentUserRoles={currentUserRoles}
          allAccounts={allAccounts}
          schools={schools}
          concentrations={concentrations}
          associations={associations}
          periods={periods}
          onClose={() => setShowAccountModal(false)}
          onSave={async () => {
            setShowAccountModal(false);
            await fetchAllData();
          }}
          onAccountsChanged={fetchAllData}
        />
      )}

    </div>
  );
};

function CsvDownloadModal({ accountCount, includeGradeLevel, onIncludeGradeLevelChange, onDownload, onClose }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 998,
          backgroundColor: 'rgba(0, 0, 0, 0.3)'
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: '#fff',
          borderRadius: '12px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid #e5e7eb',
          zIndex: 999,
          width: 'min(440px, 95vw)'
        }}
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
            Download CSV
          </h3>
          <p style={{ margin: '8px 0 0 0', fontSize: '0.875rem', color: '#6b7280', lineHeight: 1.45 }}>
            Export the currently filtered list ({accountCount} {accountCount === 1 ? 'user' : 'users'}).
          </p>
        </div>

        <div style={{ padding: '16px 24px' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '0.75rem' }}>
            Output columns
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
            {['First Name', 'Last Name', 'Email'].map((label) => (
              <label
                key={label}
                style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151' }}
              >
                <input type="checkbox" checked disabled />
                <span>{label}</span>
              </label>
            ))}

            <label
              style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}
            >
              <input
                type="checkbox"
                checked={includeGradeLevel}
                onChange={(e) => onIncludeGradeLevelChange(e.target.checked)}
                style={{ marginTop: '0.2rem' }}
              />
              <span>
                <span style={{ fontWeight: 500 }}>Grade Level</span>
                <span style={{ display: 'block', fontSize: '0.8125rem', color: '#6b7280', marginTop: '0.15rem', lineHeight: 1.4 }}>
                  Pulled from Student access card enrollments. When filtering by Term or Association, only matching enrollments are used.
                </span>
              </span>
            </label>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', padding: '16px 24px', borderTop: '1px solid #e5e7eb' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              backgroundColor: '#fff',
              color: '#374151',
              cursor: 'pointer',
              fontSize: '0.875rem'
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onDownload}
            disabled={accountCount === 0}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: 'none',
              backgroundColor: accountCount === 0 ? '#93c5fd' : '#2563eb',
              color: '#fff',
              cursor: accountCount === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            Download
          </button>
        </div>
      </div>
    </>
  );
}

function AccountsTable({ accounts, onEdit, onDelete, onCreate, currentUserRoles = [] }) {
  // Check URL parameters for accountdeletions=allowed
  const urlParams = new URLSearchParams(window.location.search);
  const allowDeletions = urlParams.get('accountdeletions') === 'allowed';
  
  // Helper function to check if account has Super - System Admin access cards
  const hasSuperSystemAdmin = (account) => {
    return account.accesscards?.some(card => card.role === 'Super - System Admin');
  };
  const hasOwnerRole = (account) => {
    return account.accesscards?.some(card => card.role === 'Owner');
  };
  
  // Helper function to check if user can edit this account
  const canEditAccount = (account) => {
    if (hasSuperSystemAdmin(account) && !currentUserRoles.includes('Super - System Admin')) {
      return false;
    }
    if (hasOwnerRole(account) && !currentUserRoles.includes('Owner')) {
      return false;
    }
    return true;
  };

  const styles = {
    container: {
      position: 'relative',
      paddingBottom: '80px' // Space for floating button
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.875rem'
    },
    headerRow: {
      backgroundColor: '#f8f9fa',
      borderBottom: '1px solid #e9ecef'
    },
    headerCell: {
      padding: '0.75rem 1rem',
      textAlign: 'left',
      fontWeight: '500',
      color: '#6c757d',
      fontSize: '0.8125rem',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    row: {
      borderBottom: '1px solid #f1f3f5',
      cursor: 'pointer',
      transition: 'background-color 0.15s ease'
    },
    rowDisabled: {
      borderBottom: '1px solid #f1f3f5',
      cursor: 'not-allowed',
      opacity: 0.6,
      transition: 'background-color 0.15s ease'
    },
    rowHover: {
      backgroundColor: '#e7f3ff'
    },
    cell: {
      padding: '0.75rem 1rem',
      color: '#495057',
      fontSize: '0.875rem'
    },
    nameCell: {
      fontWeight: '500',
      color: '#212529'
    },
    emailCell: {
      color: '#6c757d'
    },
    badgeCell: {
      display: 'flex',
      alignItems: 'center'
    },
    badge: {
      display: 'inline-block',
      padding: '0.125rem 0.5rem',
      backgroundColor: '#e9ecef',
      borderRadius: '12px',
      fontSize: '0.75rem',
      fontWeight: '500',
      color: '#6c757d'
    },
    actionsCell: {
      width: '100px',
      textAlign: 'right'
    },
    deleteButton: {
      padding: '0.25rem 0.75rem',
      backgroundColor: 'transparent',
      color: '#dc3545',
      border: '1px solid #dc3545',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8125rem',
      fontWeight: '500',
      transition: 'all 0.15s ease'
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      color: '#6c757d',
      fontSize: '0.875rem'
    },
    floatingButton: {
      position: 'fixed',
      bottom: '2rem',
      right: '2rem',
      width: '56px',
      height: '56px',
      borderRadius: '50%',
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none',
      boxShadow: '0 4px 12px rgba(0, 123, 255, 0.4)',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      fontWeight: '300',
      transition: 'all 0.2s ease',
      zIndex: 1000
    }
  };

  return (
    <div style={styles.container}>
      <table style={styles.table}>
        <thead>
          <tr style={styles.headerRow}>
            <th style={styles.headerCell}>Name</th>
            <th style={styles.headerCell}>Email</th>
            <th style={styles.headerCell}>Access Cards</th>
            {allowDeletions && <th style={styles.headerCell}>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {accounts.map(acc => {
            const canEdit = canEditAccount(acc);
            return (
              <tr
                key={acc.account_id}
                onClick={() => {
                  if (canEdit) {
                    onEdit(acc);
                  } else {
                    const blockedMessage = hasOwnerRole(acc)
                      ? 'You do not have permission to edit Owner accounts.'
                      : 'You do not have permission to edit Super - System Admin accounts.';
                    alert(blockedMessage);
                  }
                }}
                style={canEdit ? styles.row : styles.rowDisabled}
                onMouseEnter={(e) => {
                  if (canEdit) {
                    e.currentTarget.style.backgroundColor = styles.rowHover.backgroundColor;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                title={!canEdit ? (hasOwnerRole(acc) ? 'You do not have permission to edit Owner accounts.' : 'You do not have permission to edit Super - System Admin accounts.') : ''}
              >
              <td style={{ ...styles.cell, ...styles.nameCell }}>
                {acc.first_name} {acc.last_name}
              </td>
              <td style={{ ...styles.cell, ...styles.emailCell }}>
                {acc.primary_email}
              </td>
              <td style={{ ...styles.cell, ...styles.badgeCell }}>
                <span style={styles.badge}>
                  {Array.isArray(acc.accesscards) ? acc.accesscards.length : 0}
                </span>
              </td>
              {allowDeletions && (
                <td style={{ ...styles.cell, ...styles.actionsCell }}>
                  <button
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent row click from firing
                      onDelete('Account', acc.account_id);
                    }}
                    style={styles.deleteButton}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#dc3545';
                      e.currentTarget.style.color = 'white';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = '#dc3545';
                    }}
                  >
                    Delete
                  </button>
                </td>
              )}
              </tr>
            );
          })}
        </tbody>
      </table>
      
      {accounts.length === 0 && (
        <div style={styles.emptyState}>
          No accounts found
        </div>
      )}

      <button
          onClick={onCreate}
          style={styles.floatingButton}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = '#0056b3';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0, 123, 255, 0.5)';
          e.currentTarget.style.transform = 'scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = styles.floatingButton.backgroundColor;
          e.currentTarget.style.boxShadow = styles.floatingButton.boxShadow;
          e.currentTarget.style.transform = 'scale(1)';
        }}
        title="Create Account"
      >
        +
      </button>
    </div>
  );
}


// Send Reset Link button (used in AccountModal when editing)
function SendResetLinkButton({ accountId, sendingResetLink, setSendingResetLink, resetLinkUrl, setResetLinkUrl }) {
  const handleSendResetLink = async () => {
    setSendingResetLink(true);
    setResetLinkUrl(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post('/api/temp-links', {
        action: 'reset-password',
        metadata: { account_id: accountId },
        sendEmail: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResetLinkUrl(res.data.url);
      if (res.data.emailSent) {
        alert('Reset link sent to account email.');
      } else if (res.data.emailSent === false) {
        alert('Link created but email could not be sent. Copy the link below to share manually.');
      }
    } catch (err) {
      alert(`Failed to create reset link: ${err.response?.data?.error || err.message}`);
    } finally {
      setSendingResetLink(false);
    }
  };

  const handleCopy = () => {
    if (resetLinkUrl) {
      navigator.clipboard.writeText(resetLinkUrl);
      alert('Link copied to clipboard');
    }
  };

  if (resetLinkUrl) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <input
          type="text"
          readOnly
          value={resetLinkUrl}
          style={{ width: 280, padding: '0.25rem 0.5rem', fontSize: '0.75rem', border: '1px solid #dee2e6', borderRadius: 4 }}
        />
        <button type="button" onClick={handleCopy} style={linkButtonStyle}>
          Copy
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSendResetLink}
      disabled={sendingResetLink}
      style={{ ...linkButtonStyle, opacity: sendingResetLink ? 0.6 : 1 }}
    >
      {sendingResetLink ? 'Creating...' : 'Send Reset Link'}
    </button>
  );
}

const linkButtonStyle = {
  padding: '0.375rem 0.75rem',
  backgroundColor: '#6c757d',
  color: 'white',
  border: 'none',
  borderRadius: 4,
  cursor: 'pointer',
  fontSize: '0.8125rem',
  fontWeight: 500
};

function parseEnrollmentTors(enrollment) {
  if (!enrollment) return [];
  const raw = enrollment.tors;
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter((t) => t && t.account_id);
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw || '[]');
      return Array.isArray(p) ? p.filter((t) => t && t.account_id) : [];
    } catch {
      return [];
    }
  }
  return [];
}

function formatTorsDisplayText(enrollment) {
  const list = parseEnrollmentTors(enrollment);
  if (list.length === 0) return 'None';
  return list
    .map((t) => {
      const name = `${t.first_name || ''} ${t.last_name || ''}`.trim();
      if (name) return t.email ? `${name} (${t.email})` : name;
      return t.email || t.account_id || '';
    })
    .filter(Boolean)
    .join('; ');
}

/** Keys must match server utils/notificationPreferences.TOR_RELATIONSHIP_CHANNEL_KEYS */
const TOR_NOTIFY_CHANNELS = [
  { key: 'directory_assignment_submit', label: 'Directory: new assignment submission' },
  { key: 'directory_assignment_resubmit', label: 'Directory: assignment resubmission' },
  { key: 'lms_assignment_submit', label: 'LMS assignment: new submission' },
  { key: 'lms_assignment_resubmit', label: 'LMS assignment: resubmission' },
  { key: 'directory_project_comment', label: 'Comments on directory / project work' },
  { key: 'lms_assignment_comment', label: 'Comments on LMS assignment submission' }
];

function initialTorNotifyState(tor) {
  const np = tor?.notification_preferences;
  let entry = null;
  if (np && typeof np === 'object' && Array.isArray(np.relationships)) {
    entry =
      np.relationships.find((x) => x && String(x.id) === String(tor.relationship_id)) || null;
  }
  const state = {};
  for (const { key } of TOR_NOTIFY_CHANNELS) {
    state[key] = !(entry && entry[key] === false);
  }
  return state;
}

function TorNotificationControls({ tor, disabled, onSaved }) {
  const [vals, setVals] = useState(() => initialTorNotifyState(tor));
  const [saving, setSaving] = useState(false);

  const prefsSerialized =
    tor?.notification_preferences != null
      ? JSON.stringify(tor.notification_preferences)
      : '';

  useEffect(() => {
    setVals(initialTorNotifyState(tor));
  }, [tor?.relationship_id, tor?.account_id, prefsSerialized]);

  const save = async () => {
    if (!tor?.relationship_id || !tor?.account_id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `/api/user-management/account/${tor.account_id}/notification-preferences/relationship/${tor.relationship_id}`,
        vals,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (typeof onSaved === 'function') await onSaved();
    } catch (e) {
      alert(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        marginTop: '0.5rem',
        padding: '0.5rem 0.5rem 0.5rem 0.65rem',
        borderLeft: '2px solid #dee2e6',
        backgroundColor: '#f8f9fa',
        borderRadius: 4
      }}
    >
      <div
        style={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: '#495057',
          marginBottom: '0.35rem'
        }}
      >
        Email notifications (this teacher × this enrollment)
      </div>
      {TOR_NOTIFY_CHANNELS.map(({ key, label }) => (
        <label
          key={key}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            fontSize: '0.75rem',
            marginBottom: '0.25rem',
            cursor: disabled || saving ? 'default' : 'pointer',
            color: '#495057'
          }}
        >
          <input
            type="checkbox"
            checked={!!vals[key]}
            disabled={disabled || saving}
            onChange={(e) => setVals((v) => ({ ...v, [key]: e.target.checked }))}
          />
          <span>{label}</span>
        </label>
      ))}
      <button
        type="button"
        onClick={save}
        disabled={disabled || saving}
        style={{
          marginTop: '0.35rem',
          padding: '0.25rem 0.5rem',
          fontSize: '0.6875rem',
          fontWeight: 600,
          backgroundColor: saving || disabled ? '#e9ecef' : '#0d6efd',
          color: saving || disabled ? '#adb5bd' : 'white',
          border: `1px solid ${saving || disabled ? '#dee2e6' : '#0d6efd'}`,
          borderRadius: 4,
          cursor: saving || disabled ? 'not-allowed' : 'pointer'
        }}
      >
        {saving ? 'Saving…' : 'Save notification prefs'}
      </button>
    </div>
  );
}

// Modal Components
function AccountModal({
  account,
  currentUserRoles = [],
  allAccounts = [],
  schools,
  concentrations,
  associations,
  periods,
  onClose,
  onSave,
  onAccountsChanged
}) {
  const { useEffect } = React;
  const [sendingResetLink, setSendingResetLink] = useState(false);
  const [resetLinkUrl, setResetLinkUrl] = useState(null);
  const [sendingCreateLink, setSendingCreateLink] = useState(false);
  const [createLinkUrl, setCreateLinkUrl] = useState(null);
  const [formData, setFormData] = useState({
    first_name: account?.first_name || '',
    last_name: account?.last_name || '',
    primary_email: account?.primary_email || '',
    dob: account?.dob ? new Date(account.dob).toISOString().split('T')[0] : '',
    signature: account?.signature || '',
    password: '',
    // Access card fields (for new accounts)
    accesscard_email: '',
    role: 'Student',
    school_id: '',
    global: false,
    // Enrollment fields (for students)
    start_date: '',
    end_date: '',
    gradelevel: '',
    concentration: '',
    association: '',
    period_id: ''
  });

  const [loading, setLoading] = useState(false);
  const signaturePadRef = useRef(null);
  const [hasSignatureChange, setHasSignatureChange] = useState(false);
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  
  // Store original account data for comparison
  const [originalData, setOriginalData] = useState(null);
  
  // Modal states for nested editing
  const [showAccessCardModal, setShowAccessCardModal] = useState(false);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [editingAccessCard, setEditingAccessCard] = useState(null);
  const [editingEnrollment, setEditingEnrollment] = useState(null);
  const [localAccount, setLocalAccount] = useState(account);
  const assignableRoles = useMemo(() => getAssignableRoles(currentUserRoles), [currentUserRoles]);
  const accountHasOwnerRole = localAccount?.accesscards?.some((card) => card.role === 'Owner');
  const canEditOwnerCards = !accountHasOwnerRole || currentUserRoles.includes('Owner');

  useEffect(() => {
    setLocalAccount(account);
  }, [account]);

  const refreshAccountAndEnrollment = async () => {
    if (!localAccount?.account_id) return;
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`/api/user-management/account/${localAccount.account_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocalAccount(res.data.account);
      const enrId = editingEnrollment?.enrollment_id;
      if (enrId && res.data.account?.accesscards) {
        for (const card of res.data.account.accesscards) {
          const found = card.enrollments?.find((e) => e.enrollment_id === enrId);
          if (found) {
            setEditingEnrollment(found);
            break;
          }
        }
      }
      if (typeof onAccountsChanged === 'function') await onAccountsChanged();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  };

  // Load existing signature when editing
  useEffect(() => {
    if (account?.signature && signaturePadRef.current) {
      // If there's an existing signature, we can't load it into the canvas
      // The signature will remain in formData.signature and be used if no new signature is drawn
    }
  }, [account, signaturePadRef.current]);

  // Update form data when account prop changes (for editing)
  useEffect(() => {
    if (account) {
      const initialData = {
        first_name: account.first_name || '',
        last_name: account.last_name || '',
        primary_email: account.primary_email || '',
        dob: account.dob ? new Date(account.dob).toISOString().split('T')[0] : '',
        signature: account.signature || '',
        password: ''
      };
      setFormData({
        ...initialData,
        // Access card fields (for new accounts)
        accesscard_email: '',
        role: 'Student',
        school_id: '',
        global: false,
        // Enrollment fields (for students)
        start_date: '',
        end_date: '',
        gradelevel: '',
        concentration: '',
        association: '',
        period_id: ''
      });
      setOriginalData(initialData);
      setHasSignatureChange(false);
    } else {
      setOriginalData(null);
      setHasSignatureChange(false);
    }
  }, [account]);
  
  const handleSendCreateAccountLink = async () => {
    if (!formData.first_name || !formData.last_name || !formData.primary_email) {
      alert('First name, last name, and email are required.');
      return;
    }
    if (!formData.global && !formData.school_id) {
      alert('School is required unless global access is set.');
      return;
    }
    if (!formData.accesscard_email) {
      alert('Access card email is required.');
      return;
    }
    setSendingCreateLink(true);
    setCreateLinkUrl(null);
    try {
      const token = localStorage.getItem('token');
      const metadata = {
        firstName: formData.first_name,
        lastName: formData.last_name,
        email: formData.primary_email,
        accesscard_email: formData.accesscard_email,
        role: formData.role,
        school_id: formData.school_id || null,
        global: formData.global,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        gradelevel: formData.gradelevel || null,
        concentration: formData.concentration || null,
        association: formData.association || null,
        period_id: formData.period_id || null
      };
      const res = await axios.post('/api/temp-links', {
        action: 'create-account',
        metadata,
        sendEmail: true
      }, { headers: { Authorization: `Bearer ${token}` } });
      setCreateLinkUrl(res.data.url);
      if (res.data.emailSent) {
        alert('Create account link sent to ' + formData.primary_email + '.');
      } else if (res.data.emailSent === false) {
        alert('Link created but email could not be sent. Copy the link below to share manually.');
      }
    } catch (err) {
      alert(`Failed to create link: ${err.response?.data?.error || err.message}`);
    } finally {
      setSendingCreateLink(false);
    }
  };

  // Check if form has changes (only for editing existing accounts)
  const hasChanges = () => {
    if (!account || !originalData) return true; // Always allow for new accounts
    
    // Check if signature pad has changes
    if (hasSignatureChange) return true;
    
    // Check if signature pad has been drawn on
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      return true;
    }
    
    // Compare form fields
    const currentDob = formData.dob || '';
    const originalDob = originalData.dob || '';
    
    return (
      formData.first_name !== originalData.first_name ||
      formData.last_name !== originalData.last_name ||
      formData.primary_email !== originalData.primary_email ||
      currentDob !== originalDob ||
      formData.password !== ''
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Capture signature from signature pad if it exists and is not empty
      let signatureData = formData.signature;
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        signatureData = signaturePadRef.current.toDataURL('image/png');
      }

      const token = localStorage.getItem('token');
      
      if (account) {
        // Update existing account
        await axios.put(`/api/user-management/account/${account.account_id}`, {
          first_name: formData.first_name,
          last_name: formData.last_name,
          primary_email: formData.primary_email,
          dob: formData.dob || null,
          signature: signatureData,
          password: formData.password || undefined
        }, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        // Create new account with access card
        await axios.post('/api/user-management/account-with-accesscard', {
          account: {
            first_name: formData.first_name,
            last_name: formData.last_name,
            primary_email: formData.primary_email,
            dob: formData.dob || null,
            signature: signatureData,
            password: formData.password
          },
          accesscard: {
            email: formData.accesscard_email,
            role: formData.role,
            school_id: formData.school_id,
            global: formData.global
          },
          enrollment: formData.role === 'Student' ? {
            start_date: formData.start_date,
            end_date: formData.end_date,
            gradelevel: formData.gradelevel,
            concentration: formData.concentration,
            association: formData.association,
            period_id: formData.period_id
          } : null
        }, { headers: { Authorization: `Bearer ${token}` } });
      }

      onSave();
    } catch (err) {
      alert(`Failed to ${account ? 'update' : 'create'} account: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        maxWidth: 600,
        width: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fixed header with X and Update button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: 'white',
          borderRadius: '8px 8px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem 0.5rem',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
            title="Close"
          >
            ×
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {account ? (
              <SendResetLinkButton
                accountId={account.account_id}
                sendingResetLink={sendingResetLink}
                setSendingResetLink={setSendingResetLink}
                resetLinkUrl={resetLinkUrl}
                setResetLinkUrl={setResetLinkUrl}
              />
            ) : createLinkUrl ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="text"
                  readOnly
                  value={createLinkUrl}
                  style={{ width: 280, padding: '0.25rem 0.5rem', fontSize: '0.75rem', border: '1px solid #dee2e6', borderRadius: 4 }}
                />
                <button type="button" onClick={() => { navigator.clipboard.writeText(createLinkUrl); alert('Link copied to clipboard'); }} style={linkButtonStyle}>
                  Copy
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleSendCreateAccountLink}
                disabled={sendingCreateLink}
                style={{ ...linkButtonStyle, backgroundColor: '#28a745', opacity: sendingCreateLink ? 0.6 : 1 }}
              >
                {sendingCreateLink ? 'Creating...' : 'Send Link Instead'}
              </button>
            )}
            {createLinkUrl ? (
              <button
                type="button"
                onClick={onClose}
                style={{
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                Done
              </button>
            ) : (
              <button
                type="submit"
                form="account-form"
                disabled={loading || (account && !hasChanges())}
                style={{
                  padding: '0.375rem 0.75rem',
                  backgroundColor: '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: 4,
                  cursor: (loading || (account && !hasChanges())) ? 'not-allowed' : 'pointer',
                  opacity: (loading || (account && !hasChanges())) ? 0.6 : 1,
                  fontSize: '0.875rem',
                  fontWeight: '500'
                }}
              >
                {loading ? 'Saving...' : (account ? 'Update' : 'Create')}
              </button>
            )}
          </div>
        </div>
        
        {/* Scrollable content */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          flex: 1
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', marginTop: 0 }}>
            {account ? 'Edit Account' : 'Create Account'}
          </h2>
        
          <form id="account-form" onSubmit={handleSubmit}>
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>First Name *</label>
            <input
              type="text"
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              required
              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Last Name *</label>
            <input
              type="text"
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              required
              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Email *</label>
            <input
              type="email"
              value={formData.primary_email}
              onChange={(e) => setFormData({...formData, primary_email: e.target.value})}
              required
              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Date of Birth</label>
            <input
              type="date"
              value={formData.dob}
              onChange={(e) => setFormData({...formData, dob: e.target.value})}
              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
            />
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.375rem' }}>
              <label style={{ fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Signature</label>
              <button
                type="button"
                onClick={() => setShowSignaturePad(!showSignaturePad)}
                style={{
                  padding: '0.25rem 0.5rem',
                  backgroundColor: '#f8f9fa',
                  color: '#495057',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  fontWeight: '500'
                }}
              >
                {showSignaturePad ? 'Hide' : 'Show'} Signature Pad
              </button>
            </div>
            {showSignaturePad && (
              <>
                {account?.signature && !hasSignatureChange && (
                  <div style={{ marginBottom: '0.5rem', padding: '0.5rem', backgroundColor: '#f8f9fa', borderRadius: 4, border: '1px solid #dee2e6' }}>
                    <small style={{ color: '#6c757d', fontSize: '0.75rem' }}>Existing signature found. Draw a new signature below to replace it, or leave blank to keep the existing one.</small>
                  </div>
                )}
                <div style={{ border: '1px solid #dee2e6', borderRadius: 4, padding: '0.5rem' }}>
                  {SignatureCanvas ? (
                    <SignatureCanvas
                      ref={signaturePadRef}
                      onEnd={() => {
                        if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
                          setHasSignatureChange(true);
                        }
                      }}
                      canvasProps={{
                        style: {
                          width: '100%',
                          height: 120,
                          border: '1px solid #ccc',
                          borderRadius: 4
                        }
                      }}
                    />
                  ) : (
                    <p style={{ fontSize: '0.875rem', color: '#dc3545' }}>Error: Signature pad failed to load</p>
                  )}
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (signaturePadRef.current) {
                          signaturePadRef.current.clear();
                          setHasSignatureChange(false);
                        }
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: '#f8f9fa',
                        color: '#495057',
                        border: '1px solid #dee2e6',
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontSize: '0.75rem',
                        fontWeight: '500'
                      }}
                    >
                      Clear Signature
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          <div style={{ marginBottom: '0.75rem' }}>
            <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>
              Password {account ? <span style={{ fontWeight: '400', color: '#6c757d' }}>(leave blank to keep unchanged)</span> : '*'}
            </label>
            <input
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({...formData, password: e.target.value})}
              required={!account}
              autoComplete="new-password"
              style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
            />
          </div>

          {/* Display existing access cards and enrollments for editing */}
          {account && (
            <div style={{ marginTop: '1.5rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <h3 style={{ color: '#495057', fontSize: '1rem', fontWeight: '600', margin: 0 }}>
                  Access Cards & Enrollments
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    if (!canEditOwnerCards) {
                      alert('Only Owner can edit access cards for Owner accounts.');
                      return;
                    }
                    setEditingAccessCard({ account_id: account.account_id });
                    setShowAccessCardModal(true);
                  }}
                  style={{
                    padding: '0.375rem 0.75rem',
                    backgroundColor: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: '0.75rem',
                    fontWeight: '500'
                  }}
                >
                  Create New Access Card
                </button>
              </div>
              
                            {localAccount?.accesscards && localAccount.accesscards.length > 0 ? (
                localAccount.accesscards.map((card, index) => (
                <div 
                  key={card.accesscard_id || index}
                  style={{
                    border: '1px solid #e9ecef',
                    borderRadius: '6px',
                    padding: '1rem',
                    marginBottom: '0.75rem',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <img
                        src={card.school_logo || "https://acuitycte-files.s3.us-west-2.amazonaws.com/public/acuity-tech-logo.png"}
                        alt={`${card.school_name} Logo`}
                        style={{ height: '32px', width: 'auto', objectFit: 'contain', borderRadius: '4px' }}
                      />
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                          <h4 style={{ margin: 0, color: '#495057', fontSize: '0.875rem', fontWeight: '600' }}>
                            {card.school_name || 'Global'} - {card.role}
                          </h4>
                          {card.inactive && (
                            <span style={{
                              padding: '0.125rem 0.375rem',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              borderRadius: '4px',
                              fontSize: '0.6875rem',
                              fontWeight: '500'
                            }}>
                              Inactive
                            </span>
                          )}
                        </div>
                        <p style={{ margin: 0, color: '#6c757d', fontSize: '0.8125rem' }}>{card.email}</p>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={() => {
                          if (!canEditOwnerCards) {
                            alert('Only Owner can edit access cards for Owner accounts.');
                            return;
                          }
                          setEditingAccessCard(card);
                          setShowAccessCardModal(true);
                        }}
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: '#f8f9fa',
                          color: '#495057',
                          border: '1px solid #dee2e6',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '0.75rem',
                          fontWeight: '500'
                        }}
                      >
                        Edit Card
                      </button>
                      {card.role === 'Student' && (
                        <button
                          type="button"
                          onClick={() => {
                            if (!canEditOwnerCards) {
                              alert('Only Owner can edit access cards for Owner accounts.');
                              return;
                            }
                            setEditingAccessCard(card);
                            setEditingEnrollment(null); // Create new enrollment
                            setShowEnrollmentModal(true);
                          }}
                          style={{
                            padding: '0.25rem 0.5rem',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            border: '1px solid #dee2e6',
                            borderRadius: 4,
                            cursor: 'pointer',
                            fontSize: '0.75rem',
                            fontWeight: '500'
                          }}
                        >
                          {card.enrollments && card.enrollments.length > 0 ? '+ Enrollment' : 'Create Enrollment'}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Display enrollments for this access card */}
                  {card.enrollments && card.enrollments.length > 0 && (
                    <div style={{ marginTop: '0.75rem' }}>
                      <h5 style={{ marginBottom: '0.5rem', color: '#495057', fontSize: '0.8125rem', fontWeight: '600' }}>Enrollments:</h5>
                      {card.enrollments.map((enrollment, idx) => (
                        <div 
                          key={enrollment.enrollment_id || idx}
                          style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            gap: '0.5rem',
                            padding: '0.5rem',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            marginBottom: '0.375rem',
                            border: '1px solid #e9ecef'
                          }}
                        >
                          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                            <div style={{ fontSize: '0.8125rem', color: '#495057' }}>
                              <strong>Start:</strong> {enrollment.start_date} 
                              {enrollment.end_date && (
                                <span style={{ marginLeft: '0.75rem' }}>
                                  <strong>End:</strong> {enrollment.end_date}
                                </span>
                              )}
                            </div>
                            {enrollment.gradelevel && (
                              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                Grade: {enrollment.gradelevel}
                              </div>
                            )}
                            {enrollment.concentration_title && (
                              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                Concentration: {enrollment.concentration_title}
                              </div>
                            )}
                            {enrollment.association_title && (
                              <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                                Association: {enrollment.association_title}
                              </div>
                            )}
                            <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#495057' }}>
                              <strong>Teacher of record:</strong>{' '}
                              <span style={{ color: '#6c757d', fontWeight: 400 }}>{formatTorsDisplayText(enrollment)}</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              if (!canEditOwnerCards) {
                                alert('Only Owner can edit access cards for Owner accounts.');
                                return;
                              }
                              setEditingAccessCard(card);
                              setEditingEnrollment(enrollment);
                              setShowEnrollmentModal(true);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#f8f9fa',
                              color: '#495057',
                              border: '1px solid #dee2e6',
                              borderRadius: 4,
                              cursor: 'pointer',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              flexShrink: 0,
                              alignSelf: 'flex-start'
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div style={{ 
                padding: '1.5rem', 
                textAlign: 'center', 
                color: '#6c757d', 
                backgroundColor: '#f8f9fa', 
                borderRadius: '6px',
                border: '1px dashed #dee2e6'
              }}>
                <p style={{ margin: 0, fontSize: '0.8125rem', marginBottom: '0.5rem' }}>No access cards found for this account.</p>
                <p style={{ margin: 0, fontSize: '0.8125rem' }}>Click "Create New Access Card" to add one.</p>
              </div>
            )}
            </div>
          )}

          {!account && (
            <>
              <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600', color: '#495057' }}>Access Card</h3>
              
              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Access Card Email *</label>
                <input
                  type="email"
                  value={formData.accesscard_email}
                  onChange={(e) => setFormData({...formData, accesscard_email: e.target.value})}
                  required
                  style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                />
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  required
                  style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                >
                  {assignableRoles.map(role => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>
                  School {!formData.global && '*'}
                </label>
                <select
                  value={formData.school_id}
                  onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                  required={!formData.global}
                  disabled={formData.global}
                  style={{ 
                    width: '100%', 
                    padding: '0.375rem 0.5rem', 
                    border: '1px solid #dee2e6', 
                    borderRadius: 4,
                    backgroundColor: formData.global ? '#f5f5f5' : 'white',
                    fontSize: '0.875rem'
                  }}
                >
                  <option value="">{formData.global ? 'Not required for global access' : 'Select a school'}</option>
                  {schools.map(school => (
                    <option key={school.school_id} value={school.school_id}>
                      {school.school_name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '0.75rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#495057' }}>
                  <input
                    type="checkbox"
                    checked={formData.global}
                    onChange={(e) => setFormData({...formData, global: e.target.checked, school_id: e.target.checked ? '' : formData.school_id})}
                  />
                  Global Access
                </label>
              </div>

              {formData.role === 'Student' && (
                <>
                  <h3 style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1rem', fontWeight: '600', color: '#495057' }}>Enrollment</h3>
                  
                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Start Date *</label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                      required
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>End Date</label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Grade Level</label>
                    <input
                      type="text"
                      value={formData.gradelevel}
                      onChange={(e) => setFormData({...formData, gradelevel: e.target.value})}
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    />
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Concentration</label>
                    <select
                      value={formData.concentration}
                      onChange={(e) => setFormData({...formData, concentration: e.target.value})}
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    >
                      <option value="">Select a concentration</option>
                      {concentrations.map(conc => (
                        <option key={conc.id} value={conc.id}>
                          {conc.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Association</label>
                    <select
                      value={formData.association}
                      onChange={(e) => setFormData({...formData, association: e.target.value})}
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    >
                      <option value="">Select an association</option>
                      {associations.map(assoc => (
                        <option key={assoc.id} value={assoc.id}>
                          {assoc.title}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={{ marginBottom: '0.75rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Period</label>
                    <select
                      value={formData.period_id}
                      onChange={(e) => setFormData({...formData, period_id: e.target.value})}
                      style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
                    >
                      <option value="">Select an Period</option>
                      {periods.map(period => (
                        <option key={period.period_id} value={period.period_id}>
                          {period.name} ({period.start_date} - {period.end_date})
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </>
          )}

          </form>
        </div>
      </div>

      {/* Nested Access Card Modal */}
      {showAccessCardModal && (
        <NestedAccessCardModal
          accessCard={editingAccessCard}
          currentUserRoles={currentUserRoles}
          schools={schools}
          onClose={() => {
            setShowAccessCardModal(false);
            setEditingAccessCard(null);
          }}
          onSave={async () => {
            setShowAccessCardModal(false);
            setEditingAccessCard(null);
            await onSave();
          }}
        />
      )}

      {/* Nested Enrollment Modal */}
      {showEnrollmentModal && (
        <NestedEnrollmentModal
          enrollment={editingEnrollment}
          accessCard={editingAccessCard}
          allAccounts={allAccounts}
          concentrations={concentrations}
          associations={associations}
          periods={periods}
          onEnrollmentUpdated={refreshAccountAndEnrollment}
          onClose={() => {
            setShowEnrollmentModal(false);
            setEditingEnrollment(null);
            setEditingAccessCard(null);
          }}
          onSave={async () => {
            setShowEnrollmentModal(false);
            setEditingEnrollment(null);
            setEditingAccessCard(null);
            await onSave();
          }}
        />
      )}
    </div>
  );
}

function AccessCardModal({ accessCard, accounts, schools, onClose, onSave, currentUserRoles = [] }) {
  const [formData, setFormData] = useState({
    account_id: accessCard?.account_id || '',
    email: accessCard?.email || '',
    role: accessCard?.role || 'Student',
    school_id: accessCard?.school_id || '',
    global: accessCard?.global || false
  });

  const [loading, setLoading] = useState(false);
  const assignableRoles = useMemo(() => getAssignableRoles(currentUserRoles), [currentUserRoles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (accessCard) {
        await axios.put(`/api/user-management/accesscard/${accessCard.accesscard_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/user-management/accesscard', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave();
    } catch (err) {
      alert(`Failed to ${accessCard ? 'update' : 'create'} access card: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        maxWidth: 500,
        width: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fixed header with X and Update/Create button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: 'white',
          borderRadius: '8px 8px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem 0.5rem',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
            title="Close"
          >
            ×
          </button>
          <button
            type="submit"
            form="accesscard-form"
            disabled={loading}
            style={{
              padding: '0.375rem 0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : (accessCard ? 'Update' : 'Create')}
          </button>
        </div>
        
        {/* Scrollable content */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          flex: 1
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', marginTop: 0 }}>
            {accessCard ? 'Edit Access Card' : 'Create Access Card'}
          </h2>
        
          <form id="accesscard-form" onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Account *</label>
              <select
                value={formData.account_id}
                onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select an account</option>
                {accounts.map(account => (
                  <option key={account.account_id} value={account.account_id}>
                    {account.first_name} {account.last_name} ({account.primary_email})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Role *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                {assignableRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>
                School {!formData.global && '*'}
              </label>
              <select
                value={formData.school_id}
                onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                required={!formData.global}
                disabled={formData.global}
                style={{ 
                  width: '100%', 
                  padding: '0.375rem 0.5rem', 
                  border: '1px solid #dee2e6', 
                  borderRadius: 4,
                  backgroundColor: formData.global ? '#f5f5f5' : 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">{formData.global ? 'Not required for global access' : 'Select a school'}</option>
                {schools.map(school => (
                  <option key={school.school_id} value={school.school_id}>
                    {school.school_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#495057' }}>
                <input
                  type="checkbox"
                  checked={formData.global}
                  onChange={(e) => setFormData({...formData, global: e.target.checked, school_id: e.target.checked ? '' : formData.school_id})}
                />
                Global Access
              </label>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EnrollmentModal({ enrollment, accessCards, concentrations, associations, periods, onClose, onSave }) {
  const [formData, setFormData] = useState({
    accesscard_id: enrollment?.accesscard_id || '',
    start_date: enrollment?.start_date || '',
    end_date: enrollment?.end_date || '',
    gradelevel: enrollment?.gradelevel || '',
    concentration: enrollment?.concentration || '',
    association: enrollment?.association || '',
    period_id: enrollment?.period_id || ''
  });

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (enrollment) {
        await axios.put(`/api/user-management/enrollment/${enrollment.enrollment_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post('/api/user-management/enrollment', formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave();
    } catch (err) {
      alert(`Failed to ${enrollment ? 'update' : 'create'} enrollment: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        maxWidth: 500,
        width: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fixed header with X and Update/Create button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: 'white',
          borderRadius: '8px 8px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem 0.5rem',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
            title="Close"
          >
            ×
          </button>
          <button
            type="submit"
            form="enrollment-form"
            disabled={loading}
            style={{
              padding: '0.375rem 0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : (enrollment ? 'Update' : 'Create')}
          </button>
        </div>
        
        {/* Scrollable content */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          flex: 1
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', marginTop: 0 }}>
            {enrollment ? 'Edit Enrollment' : 'Create Enrollment'}
          </h2>
        
          <form id="enrollment-form" onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Access Card (Student) *</label>
              <select
                value={formData.accesscard_id}
                onChange={(e) => setFormData({...formData, accesscard_id: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select a student access card</option>
                {accessCards.map(card => (
                  <option key={card.accesscard_id} value={card.accesscard_id}>
                    {card.email} ({card.first_name} {card.last_name})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Grade Level</label>
              <input
                type="text"
                value={formData.gradelevel}
                onChange={(e) => setFormData({...formData, gradelevel: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Concentration</label>
              <select
                value={formData.concentration}
                onChange={(e) => setFormData({...formData, concentration: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select a concentration</option>
                {concentrations.map(conc => (
                  <option key={conc.id} value={conc.id}>
                    {conc.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Association</label>
              <select
                value={formData.association}
                onChange={(e) => setFormData({...formData, association: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select an association</option>
                {associations.map(assoc => (
                  <option key={assoc.id} value={assoc.id}>
                    {assoc.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Period</label>
              <select
                value={formData.period_id}
                onChange={(e) => setFormData({...formData, period_id: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select an Period</option>
                {periods.map(period => (
                  <option key={period.period_id} value={period.period_id}>
                    {period.name} ({period.start_date} - {period.end_date})
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Nested Modal Components for use within AccountModal
function NestedAccessCardModal({ accessCard, schools, onClose, onSave, currentUserRoles = [] }) {
  const [formData, setFormData] = useState({
    email: accessCard?.email || '',
    role: accessCard?.role || 'Student',
    school_id: accessCard?.school_id || '',
    global: accessCard?.global || false,
    inactive: accessCard?.inactive || false
  });

  const [loading, setLoading] = useState(false);
  const assignableRoles = useMemo(() => getAssignableRoles(currentUserRoles), [currentUserRoles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (accessCard.accesscard_id) {
        // Update existing access card
        await axios.put(`/api/user-management/accesscard/${accessCard.accesscard_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new access card
        await axios.post('/api/user-management/accesscard', {
          ...formData,
          account_id: accessCard.account_id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave();
    } catch (err) {
      alert(`Failed to ${accessCard.accesscard_id ? 'update' : 'create'} access card: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        maxWidth: 500,
        width: '90%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fixed header with X and Update/Create button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: 'white',
          borderRadius: '8px 8px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem 0.5rem',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
            title="Close"
          >
            ×
          </button>
          <button
            type="submit"
            form="nested-accesscard-form"
            disabled={loading}
            style={{
              padding: '0.375rem 0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : (accessCard.accesscard_id ? 'Update' : 'Create')}
          </button>
        </div>
        
        {/* Scrollable content */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          flex: 1
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1.5rem', marginTop: 0 }}>
            {accessCard.accesscard_id ? 'Edit Access Card' : 'Create Access Card'}
          </h2>
        
          <form id="nested-accesscard-form" onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Role *</label>
              <select
                value={formData.role}
                onChange={(e) => setFormData({...formData, role: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                {assignableRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>
                School {!formData.global && '*'}
              </label>
              <select
                value={formData.school_id}
                onChange={(e) => setFormData({...formData, school_id: e.target.value})}
                required={!formData.global}
                disabled={formData.global}
                style={{ 
                  width: '100%', 
                  padding: '0.375rem 0.5rem', 
                  border: '1px solid #dee2e6', 
                  borderRadius: 4,
                  backgroundColor: formData.global ? '#f5f5f5' : 'white',
                  fontSize: '0.875rem'
                }}
              >
                <option value="">{formData.global ? 'Not required for global access' : 'Select a school'}</option>
                {schools.map(school => (
                  <option key={school.school_id} value={school.school_id}>
                    {school.school_name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#495057' }}>
                <input
                  type="checkbox"
                  checked={formData.global}
                  onChange={(e) => setFormData({...formData, global: e.target.checked, school_id: e.target.checked ? '' : formData.school_id})}
                />
                Global Access
              </label>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem', color: '#495057' }}>
                <input
                  type="checkbox"
                  checked={formData.inactive}
                  onChange={(e) => setFormData({...formData, inactive: e.target.checked})}
                />
                Inactive
              </label>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function NestedEnrollmentModal({
  enrollment,
  accessCard,
  allAccounts = [],
  concentrations,
  associations,
  periods,
  onClose,
  onSave,
  onEnrollmentUpdated
}) {
  const [formData, setFormData] = useState({
    start_date: enrollment?.start_date || '',
    end_date: enrollment?.end_date || '',
    gradelevel: enrollment?.gradelevel || '',
    concentration: enrollment?.concentration_id || '',
    association: enrollment?.association_id || '',
    period_id: enrollment?.period_id || ''
  });

  const [loading, setLoading] = useState(false);
  const [addTorId, setAddTorId] = useState('');
  const [torBusy, setTorBusy] = useState(false);

  useEffect(() => {
    setFormData({
      start_date: enrollment?.start_date || '',
      end_date: enrollment?.end_date || '',
      gradelevel: enrollment?.gradelevel || '',
      concentration: enrollment?.concentration_id || '',
      association: enrollment?.association_id || '',
      period_id: enrollment?.period_id || ''
    });
  }, [enrollment]);

  const instructorOptions = useMemo(() => {
    if (!allAccounts?.length) return [];
    const rows = [];
    for (const acc of allAccounts) {
      const hasInstructor = acc.accesscards?.some(
        (c) => c.role === 'Instructor' && !c.inactive
      );
      if (!hasInstructor) continue;
      const label = `${acc.first_name || ''} ${acc.last_name || ''} (${acc.primary_email || ''})`.trim();
      rows.push({ account_id: acc.account_id, label: label || String(acc.account_id) });
    }
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return rows;
  }, [allAccounts]);

  const torsList = useMemo(() => parseEnrollmentTors(enrollment), [enrollment]);
  const assignedTorAccountIds = useMemo(
    () => new Set(torsList.map((t) => String(t.account_id))),
    [torsList]
  );
  const addTorCandidates = useMemo(
    () => instructorOptions.filter((o) => !assignedTorAccountIds.has(String(o.account_id))),
    [instructorOptions, assignedTorAccountIds]
  );

  const handleAddTor = async () => {
    if (!enrollment?.enrollment_id || !addTorId) return;
    setTorBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/user-management/enrollments/${enrollment.enrollment_id}/teachers-of-record`,
        { tor_account_id: addTorId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAddTorId('');
      if (typeof onEnrollmentUpdated === 'function') await onEnrollmentUpdated();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setTorBusy(false);
    }
  };

  const handleRemoveTor = async (relationshipId) => {
    if (!enrollment?.enrollment_id || !relationshipId) return;
    setTorBusy(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `/api/user-management/enrollments/${enrollment.enrollment_id}/teachers-of-record/${relationshipId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (typeof onEnrollmentUpdated === 'function') await onEnrollmentUpdated();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setTorBusy(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      
      if (enrollment) {
        // Update existing enrollment
        await axios.put(`/api/user-management/enrollment/${enrollment.enrollment_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Create new enrollment for the access card
        if (!accessCard || !accessCard.accesscard_id) {
          throw new Error('Access card ID is required to create an enrollment');
        }
        
        console.log('Creating enrollment with data:', {
          ...formData,
          accesscard_id: accessCard.accesscard_id
        });
        
        await axios.post('/api/user-management/enrollment', {
          ...formData,
          accesscard_id: accessCard.accesscard_id
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }

      onSave();
    } catch (err) {
      alert(`Failed to ${enrollment ? 'update' : 'create'} enrollment: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.7)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 2000
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: 8,
        maxWidth: 560,
        width: '92%',
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Fixed header with X and Update/Create button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #e9ecef',
          backgroundColor: 'white',
          borderRadius: '8px 8px 0 0',
          position: 'sticky',
          top: 0,
          zIndex: 10
        }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#6c757d',
              padding: '0.25rem 0.5rem',
              lineHeight: 1,
              borderRadius: '4px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.color = '#495057';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6c757d';
            }}
            title="Close"
          >
            ×
          </button>
          <button
            type="submit"
            form="nested-enrollment-form"
            disabled={loading}
            style={{
              padding: '0.375rem 0.75rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1,
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            {loading ? 'Saving...' : (enrollment ? 'Update' : 'Create')}
          </button>
        </div>
        
        {/* Scrollable content */}
        <div style={{
          padding: '1.5rem',
          overflowY: 'auto',
          flex: 1
        }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.75rem', marginTop: 0 }}>
            {enrollment ? 'Edit Enrollment' : 'Create Enrollment'}
          </h2>
          {accessCard && (
            <p style={{ color: '#6c757d', marginBottom: '1.5rem', fontSize: '0.8125rem' }}>
              For: {accessCard.email} ({accessCard.school_name || 'Global'} - {accessCard.role})
            </p>
          )}
        
          <form id="nested-enrollment-form" onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                required
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Grade Level</label>
              <input
                type="text"
                value={formData.gradelevel}
                onChange={(e) => setFormData({...formData, gradelevel: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              />
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Concentration</label>
              <select
                value={formData.concentration}
                onChange={(e) => setFormData({...formData, concentration: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select a concentration</option>
                {concentrations.map(conc => (
                  <option key={conc.id} value={conc.id}>
                    {conc.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Association</label>
              <select
                value={formData.association}
                onChange={(e) => setFormData({...formData, association: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select an association</option>
                {associations.map(assoc => (
                  <option key={assoc.id} value={assoc.id}>
                    {assoc.title}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.8125rem', fontWeight: '500', color: '#495057' }}>Period</label>
              <select
                value={formData.period_id}
                onChange={(e) => setFormData({...formData, period_id: e.target.value})}
                style={{ width: '100%', padding: '0.375rem 0.5rem', border: '1px solid #dee2e6', borderRadius: 4, fontSize: '0.875rem' }}
              >
                <option value="">Select an Period</option>
                {periods.map(period => (
                  <option key={period.period_id} value={period.period_id}>
                    {period.name} ({period.start_date} - {period.end_date})
                  </option>
                ))}
              </select>
            </div>

            {enrollment?.enrollment_id && (
              <div
                style={{
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e9ecef'
                }}
              >
                <label
                  style={{
                    display: 'block',
                    marginBottom: '0.375rem',
                    fontSize: '0.8125rem',
                    fontWeight: '600',
                    color: '#495057'
                  }}
                >
                  Teachers of record
                </label>
                <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: '#868e96' }}>
                  Add instructors with an active Instructor access card. Remove a row to unlink that teacher.
                </p>
                {torsList.length === 0 ? (
                  <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: '#6c757d' }}>None</p>
                ) : (
                  <ul
                    style={{
                      margin: '0 0 0.75rem',
                      paddingLeft: '1rem',
                      listStyle: 'none',
                      fontSize: '0.8125rem',
                      color: '#495057'
                    }}
                  >
                    {torsList.map((t, i) => (
                      <li
                        key={t.relationship_id || `${t.account_id}-${i}`}
                        style={{ marginBottom: '0.75rem' }}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
                          <span>
                            {`${t.first_name || ''} ${t.last_name || ''}`.trim() || t.account_id}
                            {t.email ? ` (${t.email})` : ''}
                          </span>
                          <button
                            type="button"
                            disabled={torBusy || !t.relationship_id}
                            onClick={() => handleRemoveTor(t.relationship_id)}
                            style={{
                              padding: '0.125rem 0.375rem',
                              fontSize: '0.6875rem',
                              backgroundColor: '#fff',
                              color: '#dc3545',
                              border: '1px solid #f5c2c7',
                              borderRadius: 4,
                              cursor: torBusy || !t.relationship_id ? 'not-allowed' : 'pointer'
                            }}
                            title={!t.relationship_id ? 'Refresh account data to remove' : 'Remove'}
                          >
                            Remove
                          </button>
                        </div>
                        {t.relationship_id && t.account_id ? (
                          <TorNotificationControls
                            tor={t}
                            disabled={torBusy}
                            onSaved={onEnrollmentUpdated}
                          />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center' }}>
                  <select
                    value={addTorId}
                    onChange={(e) => setAddTorId(e.target.value)}
                    disabled={torBusy || addTorCandidates.length === 0}
                    style={{
                      flex: '1 1 200px',
                      minWidth: 180,
                      padding: '0.375rem 0.5rem',
                      border: '1px solid #dee2e6',
                      borderRadius: 4,
                      fontSize: '0.875rem',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">
                      {addTorCandidates.length === 0 ? 'No more instructors to add' : 'Select instructor to add…'}
                    </option>
                    {addTorCandidates.map((opt) => (
                      <option key={opt.account_id} value={opt.account_id}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    disabled={torBusy || !addTorId}
                    onClick={handleAddTor}
                    style={{
                      padding: '0.375rem 0.75rem',
                      backgroundColor: addTorId && !torBusy ? '#198754' : '#e9ecef',
                      color: addTorId && !torBusy ? 'white' : '#adb5bd',
                      border: `1px solid ${addTorId && !torBusy ? '#198754' : '#dee2e6'}`,
                      borderRadius: 4,
                      cursor: torBusy || !addTorId ? 'not-allowed' : 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 500
                    }}
                  >
                    {torBusy ? '…' : 'Add'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

export default UserManagement; 